import React, { useRef, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Input } from './ui/input';
import { MapPin, Loader2 } from 'lucide-react';

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// ── Script loader (singleton) ──────────────────────────────────────────────
let _loadPromise = null;
function loadPlacesLibrary() {
  if (_loadPromise) return _loadPromise;
  if (window.google?.maps?.places?.AutocompleteSuggestion) {
    return (_loadPromise = Promise.resolve());
  }
  _loadPromise = new Promise((resolve, reject) => {
    if (!API_KEY) { reject(new Error('No API key')); return; }
    const existing = document.querySelector('script[src*="maps.googleapis.com"]');
    if (!existing) {
      const s = document.createElement('script');
      s.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=places&loading=async`;
      s.async = true;
      s.onerror = reject;
      document.head.appendChild(s);
    }
    const poll = setInterval(() => {
      if (window.google?.maps?.importLibrary) {
        clearInterval(poll);
        window.google.maps.importLibrary('places').then(resolve).catch(reject);
      }
    }, 100);
    setTimeout(() => { clearInterval(poll); reject(new Error('Timeout')); }, 15000);
  });
  return _loadPromise;
}

// ── Parse address components ───────────────────────────────────────────────
function parseAddressComponents(components) {
  const map = {};
  for (const c of components) {
    for (const t of c.types) map[t] = c;
  }
  const num = map.street_number?.longText || map.street_number?.long_name || '';
  const route = map.route?.longText || map.route?.long_name || '';
  return {
    street: [num, route].filter(Boolean).join(' '),
    city: map.locality?.longText || map.locality?.long_name
      || map.sublocality_level_1?.longText || map.sublocality_level_1?.long_name || '',
    state: map.administrative_area_level_1?.shortText || map.administrative_area_level_1?.short_name || '',
    zip: map.postal_code?.longText || map.postal_code?.long_name || '',
  };
}

/**
 * Reusable Google Places address autocomplete (New API).
 * Uses fetchAutocompleteSuggestions() + React portal for the dropdown
 * so it escapes parent overflow/transform clipping.
 */
export function AddressAutocomplete({
  value, onChange, onPlaceSelect, fieldPrefix = 'service_',
  placeholder = 'Start typing an address...', className = '',
  countryCode = 'us',
  countryCodes,  // optional array — when provided, takes precedence over countryCode
  ...rest
}) {
  const [ready, setReady] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState(null);

  useEffect(() => {
    if (!API_KEY) return;
    loadPlacesLibrary().then(() => setReady(true)).catch(() => {});
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (inputRef.current?.contains(e.target)) return;
      // Check if click is on the portal dropdown
      const portal = document.getElementById('address-autocomplete-portal');
      if (portal?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Position the portal dropdown relative to input
  const updatePosition = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  // Fetch suggestions
  const fetchSuggestions = useCallback(async (input) => {
    if (!ready || !input || input.length < 3) { setSuggestions([]); return; }
    setLoading(true);
    try {
      // Multi-country support: if `countryCodes` array is provided, restrict
      // autocomplete to all of them. Otherwise fall back to single `countryCode`.
      const regions = (Array.isArray(countryCodes) && countryCodes.length > 0
        ? countryCodes
        : [countryCode]
      ).map(c => String(c || '').toLowerCase()).filter(Boolean);
      const { suggestions: results } =
        await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
          includedRegionCodes: regions,
        });
      setSuggestions(results || []);
      setActiveIdx(-1);
    } catch (e) {
      console.warn('Autocomplete error:', e);
      setSuggestions([]);
    } finally { setLoading(false); }
  }, [ready, countryCode, countryCodes]);

  const handleChange = (val) => {
    onChange(val);
    clearTimeout(debounceRef.current);
    if (val.length >= 3 && ready) {
      setOpen(true);
      updatePosition();
      debounceRef.current = setTimeout(() => fetchSuggestions(val), 250);
    } else {
      setSuggestions([]);
      setOpen(false);
    }
  };

  const handleSelect = useCallback(async (suggestion) => {
    setOpen(false);
    setSuggestions([]);
    if (!suggestion?.placePrediction) return;

    const mainText = suggestion.placePrediction.text?.text || '';
    onChange(mainText);

    try {
      const place = suggestion.placePrediction.toPlace();
      await place.fetchFields({ fields: ['addressComponents', 'location'] });
      const parsed = parseAddressComponents(place.addressComponents || []);
      const lat = place.location?.lat?.() ?? null;
      const lng = place.location?.lng?.() ?? null;
      const fp = fieldPrefix;

      onPlaceSelect?.({
        [`${fp}street`]: parsed.street || mainText,
        [`${fp}city`]: parsed.city,
        [`${fp}state`]: parsed.state,
        [`${fp}zip`]: parsed.zip,
        latitude: lat,
        longitude: lng,
      });
    } catch {
      onPlaceSelect?.({ [`${fieldPrefix}street`]: mainText });
    }
  }, [onChange, onPlaceSelect, fieldPrefix]);

  const handleKeyDown = (e) => {
    if (!open || !suggestions.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  // Dropdown rendered as a portal to escape overflow/transform clipping
  const dropdown = open && suggestions.length > 0 && dropdownPos
    ? createPortal(
        <div
          id="address-autocomplete-portal"
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
            zIndex: 9999,
          }}
          data-testid="address-suggestions-dropdown"
        >
          <div className="bg-popover border rounded-lg shadow-lg max-h-56 overflow-y-auto">
            {suggestions.map((s, i) => {
              const pred = s.placePrediction;
              const main = pred?.mainText?.text || '';
              const secondary = pred?.secondaryText?.text || '';
              return (
                <button
                  key={pred?.placeId || i}
                  type="button"
                  className={`w-full text-left px-3 py-2.5 flex items-start gap-2.5 transition-colors ${
                    i === activeIdx ? 'bg-accent' : 'hover:bg-accent/50'
                  }`}
                  onMouseEnter={() => setActiveIdx(i)}
                  onClick={() => handleSelect(s)}
                  data-testid={`address-suggestion-${i}`}
                >
                  <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{main}</p>
                    {secondary && <p className="text-xs text-muted-foreground truncate">{secondary}</p>}
                  </div>
                </button>
              );
            })}
            <div className="px-3 py-1.5 border-t">
              <p className="text-[10px] text-muted-foreground/60">Powered by Google</p>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <div className="relative">
        <Input
          ref={inputRef}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => { if (suggestions.length) { updatePosition(); setOpen(true); } }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          className={className}
          {...rest}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
        )}
      </div>
      {dropdown}
    </>
  );
}
